import { Close, Info, Warning } from "@mui/icons-material";
import {
  Alert,
  AlertTitle,
  CircularProgress,
  Dialog,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import { toast } from "react-toastify";
import { PrimaryButton, SecondaryButton } from "../../../components/common";
import { PostResultJson } from "../../../services/fhir/patientsummary/documentreference/PostResultJson";
import { auxiliarData } from "@/services/fhir/AuxiliarData";
import { useSessionContext } from "@/contexts/SessionContext";
import { useDocumentTaskNotification } from "./DocumentTaskNotification";
import { PostAnnotation } from "./PostAnnotation";

export function ConfirmDocumentDialog({
  fileBase64,
  setRefetch,
  isOpenDialog,
  onCloseDialog,
  setRenderButton,
  data,
  encounterReference,
  basedOn,
  warningMessage,
}: any) {
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState("");
  const { access, user } = useSessionContext();
  const { createNotification } = useDocumentTaskNotification();
  const basedOnReference = basedOn?.map((el: any) => ({
    reference: el.reference,
  }));

  const handleConfirm = async () => {
    try {
      setLoading(true);
      // Prepara os dados do documento
      const dataAux = auxiliarData.DocumentReferenceJson(
        {
          ...data,
          organizationId:
            access?.type === "professional"
              ? access?.organizationId
              : undefined,
          practitionerId:
            access?.type === "professional" ? user?.username : undefined,
        },
        encounterReference,
        basedOnReference
      );

      // Envia o documento
      const { response, uuidValue } = await PostResultJson(data, encounterReference, basedOnReference);

      // Cria a tarefa de notificação se for um profissional
      if (access?.type === "patient") {
        // Obtém o ID do paciente e do profissional
        const patientId = encounterReference?.subject;
        const practitionerId = data?.practitionerId || user?.username;

        // Cria a notificação usando o hook local
        await createNotification({
          patientId,
          practitionerId,
          title: `O paciente ${user?.name} adicionou um adicionou um resultado de exame`,
          description: "Novo documento",
          organization: data?.organization?.id,
        });
      }

      if (comment !== "") {
        console.log("entrou com comentário", comment);
        await PostAnnotation(comment, encounterReference, basedOnReference, data?.organization?.id, user?.name, uuidValue);
      }

      toast.success("Resultado de exame enviado com sucesso!");
      if (setRenderButton) setRenderButton(false);
      setRefetch?.((prev: boolean) => !prev);
    } catch (err) {
      console.log("PostDocument", err);
      toast.warn("Não foi possível finalizar a operação, tente novamente");
    } finally {
      setLoading(false);
      onCloseDialog();
    }
  };

  const handleDialogClose = (event: any, reason: string) => {
    if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
      onCloseDialog(event, reason);
    }
  };

  const base64ToBlob = (base64: any, contentType = "") => {
    const byteCharacters = atob(base64);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  };

  const verifyDocSize = (base64String: any, limit: number) => {
    const sizeInBytes = (base64String.length * 3) / 4;

    const limitInBytes = limit * 1024 * 1024;

    return sizeInBytes > limitInBytes;
  };

  async function downloadBase64File() {
    try {
      const blob = base64ToBlob(data?.base64, "application/pdf");
      const fileUrl = URL.createObjectURL(blob);
      const auxFile = {
        fileUrl,
        fileName: "Documento.pdf",
      };

      window.open(auxFile.fileUrl, "_blank");
      // setDataRow(e.row);
      // setOpenInformation(true);
    } catch (err) {
      console.log("err", err);
    }
  }

  useEffect(() => {
    if (verifyDocSize(data?.base64, 1.5)) {
      toast.info(
        "O arquivo selecionado excede o limite de 1.5MB permitido pela plataforma."
      );
      onCloseDialog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <Dialog
      open={isOpenDialog}
      onClose={handleDialogClose}
      PaperProps={{
        sx: { padding: "8px 10px" },
      }}
      maxWidth="md"
      fullScreen={isMobile}
    >
      <Stack width={{ xs: "100%", md: "700px" }}>
        <Stack mb={5}>
          <IconButton
            onClick={() => onCloseDialog(null, "iconButtonClick")}
            sx={{ position: "absolute", right: 5, top: 5 }}
          >
            <Close />
          </IconButton>

          <DialogTitle textAlign="center">Adicionar arquivo</DialogTitle>
          {warningMessage && (
            <Alert
              severity="warning"
              icon={<Warning fontSize="medium" />}
              sx={{
                mx: 2,
                mb: 2,
                py: 1.5,
                backgroundColor: "#FFF4E5",
                border: "1px solid #FFB74D",
                "& .MuiAlert-icon": {
                  color: "#F57C00",
                },
              }}
            >
              <AlertTitle sx={{ fontWeight: "bold" }}>
                Documento não identificado
              </AlertTitle>
              {warningMessage}
            </Alert>
          )}

          {verifyDocSize(fileBase64, 1) ? (
            <Stack>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={1}
              >
                <Info sx={{ color: "#0000a7" }} />
                <Typography textAlign="justify">
                  O arquivo selecionado excede o tamanho limite de
                  pre-visualização.
                </Typography>
                <Typography
                  onClick={() => {
                    downloadBase64File();
                  }}
                  variant={"body1"}
                  color="primary"
                  sx={{
                    "&:hover": {
                      textDecoration: "underline",
                      cursor: "pointer",
                    },
                  }}
                >
                  Clique aqui para visualizar
                </Typography>
              </Stack>
            </Stack>
          ) : (
            <iframe
              title="addFile"
              src={fileBase64}
              style={{
                height: "700px",
                maxHeight: "70vh",
                width: "100%",
              }}
            />
          )}

          {/* Campo de comentário abaixo do PDF */}
          <Stack sx={{ px: { xs: 0, md: 2 }, mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              Comentário (opcional)
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              name="comments"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              size="small"
              placeholder="Escreva um contexto breve para o resultado enviado"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#F9FAFB',
                },
              }}
            />
          </Stack>
        </Stack>

        <Stack direction="row" justifyContent="space-between" gap={2} mt={1}>
          <SecondaryButton
            width="250px"
            height="50px"
            onClick={() => onCloseDialog()}
            id="goBackButton"
          >
            Voltar
          </SecondaryButton>

          <PrimaryButton
            disabled={loading}
            width="250px"
            height="50px"
            onClick={handleConfirm}
          >
            {loading ? (
              <CircularProgress size={24} color="primary" />
            ) : (
              "Confirmar"
            )}
          </PrimaryButton>
        </Stack>
      </Stack>
    </Dialog>
  );
}
